import { useCallback, useState } from 'react'
import { dateStampFormat, formatDateTimeISO, numberFormat, dateTimeStampFormat, timeStampFormat } from 'shared/util'

import { Box, Stack, Typography, TableCell, Tooltip, IconButton, FormControl, Chip } from '@mui/material'
import { Cached as ReloadIcon } from '@mui/icons-material'

import { useUserManagementContainer } from './container'

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { ValueSpan } from 'components/controls/valueSpan'
import { SearchTableBody } from 'components/controls/searchTable'
import { MultiElementCell } from 'components/controls/multiElementCel'
import { OptionSelect } from 'components/controls/optionSelect'

import { ConfigureListHeader } from '../components/header'
import { UserAvatar } from 'features/user/components/avatar'
import { UserRoleChip } from 'features/user/components/roleChip'
import { GroupChip } from 'features/user/group/components/chip'

import { UserResult } from 'interface/user'
import { InviteResult, InviteStatus } from 'interface/user/invite'

import { AddInviteDialog } from 'features/user/invite/dialog/add'
import { EditUserDialog } from 'features/user/dialog/edit'
import { InviteApplovalDialog } from 'features/user/invite/dialog/apploval';
import { EditInviteDialog } from 'features/user/invite/dialog/edit';

export const ConfigureUserPage = () => {
  const [modal, setModal] = useState<{
		mode: "addInvite" | "editUser" | "applovalInvite" | "editInvite"
    invite?: InviteResult
		user?: UserResult
  } | null>(null)

  const {
    loading,
    errors,
    groupList,
    userList,
    inviteList,
    searchGroup,
    setSearchGroup,
    searchWord,
    setSearchWord,
    onReload
  } = useUserManagementContainer()

  const closeModal = useCallback(
    () => setModal(null),
    [setModal]
  )
  const reload = useCallback(
    () => {
      onReload()
      closeModal()
    },
    [closeModal, onReload]
  )
  return (
    <>
      {loading && <Loading />}

      <Stack overflow='hidden' height="100%" width="100%">
        <ConfigureListHeader
          openAddButtonLabel="新規ユーザーを招待"
          openAddDialog={() => setModal({ mode: "addInvite" })}
          searchWord={searchWord}
          setSearchWord={setSearchWord}
          filters={
            <FormControl sx={{ minWidth: '12em' }}>
              <OptionSelect
                id='groupfilter'
                variant="outlined"
                size="small"
                fullWidth
                value={searchGroup}
                onChange={(e) => setSearchGroup(e.target.value as number[])}
                optionList={(groupList ?? []).map((group) => ({
                  id: group.groupId,
                  label: group.groupName
                }))}
                placeHolder="グループ絞り込み"
              />
            </FormControl>
          }
        >
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <ValueSpan text={numberFormat(userList?.length, '')} suffix='名 のユーザー'/>
            { inviteList.length > 0 && <ValueSpan text={numberFormat(inviteList.length)} prefix=' / ' suffix='名 の招待'/> }

            <Tooltip title="更新">
              <IconButton onClick={onReload}>
                <ReloadIcon/>
              </IconButton>
            </Tooltip>
          </Stack>
        </ConfigureListHeader>

        <Stack flexGrow={1} overflow="auto">
          {(inviteList.length > 0) && (
            <Box flexShrink={0} maxHeight="50vh">
              <SearchTableBody<InviteResult>
                list={inviteList}
                columnList={[
                  {
                    label: '状態',
                    cell: (r) => (
                      <TableCell key={`invite-${r.inviteId}-state`} sx={{ whiteSpace: "nowrap" }}>
                        {r.inviteStatus === InviteStatus.Requesting ? (
                          <Chip color='warning' size="small" label="申請"/>
                        ) : r.inviteStatus === InviteStatus.Inviting ? (
                          (r.effectiveTime && (r.effectiveTime.localeCompare(formatDateTimeISO(new Date()) ?? '') < 0)) ? (
                            <Chip color='error' size="small" label="期限"/>
                          ) : (
                            <Chip color='secondary' size="small" label="招待"/>
                          )
                        ) : <></>}
                      </TableCell>
                    )
                  },
                  {
                    label: '招待ユーザー',
                    cell: (r) => (
                      <TableCell key={`invite-${r.inviteId}-user`} sx={{ width: "100%" }}>
                        {r.userName ? (
                          <Stack direction='column' alignItems="flex-start">
                            <Typography variant="body1">{r.userName}</Typography>
                            <Typography variant="body2" color="textSecondary">{r.email}</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body1">{r.email}</Typography>
                        )}
                      </TableCell>
                    )
                  },
                  {
                    label: '割当グループ',
                    cell: (r) => (
                      <MultiElementCell
                        key={`invite-${r.inviteId}-group`}
                        notFound={<Typography variant="body2" color="textSecondary">未指定</Typography>}
                        item={(group, index) => (
                          <GroupChip
                            key={`invite-${r.inviteId}-group-${index}`}
                            group={group}
                            size="small"
                          />
                        )}
                        list={r.groups ?? []}
                        sx={{ whiteSpace: "nowrap" }}                        
                      />
                    )
                  },
                  {
                    label: '有効期限',
                    cell: (r) => (
                      <TableCell key={`invite-${r.inviteId}-effect`} sx={{ whiteSpace: "nowrap" }}>
                        {r.effectiveTime ? (
                          <Stack direction='row' alignItems="center" spacing={1}>
                            <Typography variant="body2">{dateTimeStampFormat(r.effectiveTime)}</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    )
                  },
                ]}
                onClickRow={invite => setModal({
                  mode: (
                    invite.inviteStatus === InviteStatus.Requesting ? "applovalInvite" : 
                    "editInvite"
                  ),
                  invite
                })}
              />
            </Box>
          )}

          <Stack direction="column" spacing={1}>
            {errors.map((error, i) => <ErrorInfo key={`error-${i}`} error={error}/>)}
          </Stack>

          {userList && (
            <Box flexShrink={0} flexGrow={1}>
              <SearchTableBody<UserResult>
                list={userList}
                columnList={[
                  {
                    label: '状態',
                    cell: (r) => (
                      <TableCell key={`user-${r.userId}-state`} sx={{ whiteSpace: "nowrap" }}>
                        {(r.effectiveTime && (r.effectiveTime.localeCompare(formatDateTimeISO(new Date()) ?? '') < 0)) ? (
                          <Chip color='error' size="small" label="期限"/>
                        ) : r.isLocked ? (
                          <Chip color='warning' size="small" label="ロック"/>
                        ) : (
                          <Chip color='success' size="small" label="有効"/>
                        )}
                      </TableCell>
                    )
                  },
                  {
                    label: 'ユーザー',
                    cell: (r) => (
                      <TableCell key={`user-${r.userId}-info`} sx={{ width: "100%" }}>
                        <Stack direction='row' alignItems="center" spacing={1}>
                          <UserAvatar key={`user-avator-${r.userId}`} user={r} sx={{ width: '2rem', height: '2rem' }}/>
                          <Stack direction='column' alignItems="flex-start">
                            <Typography variant="body1">{r.userName}</Typography>
                            <Typography variant="body2" color="textSecondary">{r.email}</Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                    )
                  },
                  {
                    label: '最終ログイン',
                    cell: (r) => (
                      <TableCell key={`user-${r.userId}-effect`} align="center" sx={{ whiteSpace: "nowrap" }}>
                        {r.lastestLogin ? (
                          <Stack direction='row' alignItems="center" spacing={1}>
                            <Typography variant="body2">
                              {dateStampFormat(r.lastestLogin)}
                              <br/>
                              {timeStampFormat(r.lastestLogin)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    )
                  },
                  {
                    label: '権限',
                    cell: (r) => (
                      <MultiElementCell
                        key={`users-role-${r.userId}`}
                        notFound={<Typography>なし</Typography>}
                        maxVisible={5}
                        item={(role) => (
                          <UserRoleChip
                            key={`${r.userId}-role-${role}`}
                            size="small"
                            role={role}
                          />
                        )}
                        list={r.role}
                        sx={{ minWidth: '12rem' }}
                        stackProps={{ flexWrap: 'wrap', useFlexGap: true }}
                      />
                    )
                  },
                  {
                    label: 'グループ',
                    cell: (r) => (
                      <MultiElementCell
                        key={`users-group-${r.userId}`}
                        notFound={<Typography>なし</Typography>}
                        item={(group, index) => (
                          <GroupChip
                            key={`${r.userId}-group-${index}-${group.groupId}`}
                            size="small"
                            group={group}
                          />
                        )}
                        list={r.group ?? []}
                        sx={{ minWidth: '20rem' }}
                        stackProps={{ flexWrap: 'wrap', useFlexGap: true }}
                      />
                    )
                  },
                  {
                    label: '有効期限',
                    cell: (r) => (
                      <TableCell key={`user-${r.userId}-effect`} align="center" sx={{ whiteSpace: "nowrap" }}>
                        {r.effectiveTime ? (
                          <Stack direction='row' alignItems="center" spacing={1}>
                            <Typography variant="body2">
                              {dateStampFormat(r.effectiveTime)}
                              <br/>
                              {timeStampFormat(r.effectiveTime)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    )
                  },
                ]}
                onClickRow={user => setModal({ mode: "editUser", user })}
              />
            </Box>
          )}
        </Stack>
      </Stack>

      {modal?.mode === "addInvite" ? (
        <AddInviteDialog
          groupList={groupList}
          onAddInvite={reload}
          onClose={closeModal}
        />
      ) : (modal?.mode === "editUser" && modal?.user) ? (
        <EditUserDialog
          user={modal.user}
          groupList={groupList}
          onEditUser={reload}
          onDeleteUser={reload}
          onUnlockUser={reload}
          onClose={closeModal}
        />
      ) : (modal?.mode === "applovalInvite" && modal?.invite) ? (
        <InviteApplovalDialog
          invite={modal.invite}
          groupList={groupList}
          onApplovalInvite={reload}
          onRejectInvite={reload}
          onClose={closeModal}
        />
      ) : (modal?.mode === "editInvite" && modal?.invite) ? (
        <EditInviteDialog
          invite={modal.invite}
          onExtendInvite={reload}
          onDeleteInvite={reload}
          onClose={closeModal}
        />
      ) : <></> }
    </>
  )
}
